/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { FaqSectionFragment } from "@cosmos/hygraph-schema";

import { BackgroundContainer, FAQ, ResponsiveContainer, SectionContainer, SectionHeading } from "@components/elements";
import { getFaqId, getShortId } from "@components/utils";
import { FAQButtonInput, FAQItem, mapFAQButtons, processFAQItems } from "@components/utils/faq.utils";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useMemo } from "react";

const faqStyles = css({
	marginInline: "auto",
	maxWidth: "52rem",
	width: "100%",
	display: "flex",
	alignItems: "flex-start",
	flexDirection: "column",
	gap: "eds.dimension.32",
});

export const FAQSection = ({
	config,
	items,
	refFaqItems,
	firstItemDefaultOpen,
	title,
	id,
	faqButtons,
	faqButtonsPosition,
	modButtons,
}: FaqSectionFragment): React.JSX.Element | null => {
	const faqItems: FAQItem[] = useMemo(() => {
		return processFAQItems(items, refFaqItems);
	}, [items, refFaqItems]);
	const faqId = useMemo(() => (id ? getFaqId(getShortId(id)) : undefined), [id]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(faqItems?.length || title || faqButtons?.length);

	const buttonsSource: FAQButtonInput[] = useMemo(
		() => (modButtons?.length ? modButtons : faqButtons) || [],
		[modButtons, faqButtons],
	);
	const cosmosButtons = useMemo(() => mapFAQButtons(buttonsSource), [buttonsSource]);

	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<VStack alignItems="stretch" gap="eds.dimension.48" className="faq-section">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack alignItems="stretch" gap="eds.dimension.32" id={faqId}>
								<div className={faqStyles}>
									<FAQ
										items={faqItems}
										title={title?.text}
										id={id}
										buttons={cosmosButtons}
										buttonsPosition={faqButtonsPosition || undefined}
										firstItemDefaultOpen={firstItemDefaultOpen}
									/>
								</div>
							</VStack>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
